import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import './ChatbotPage.css';
import useChatHistory from '../hooks/useChatHistory';
import { sendChatMessage, clearChat, detectUserInfo, classifySubject, getSubjectPrompt } from '../services/apiService';
import { build_dynamic_system_prompt } from '../utils/build_dynamic_system_prompt';
import { getConversationId, setConversationId, removeConversationId, getDynamicSystemPrompt as getDynamicSystemPromptFromStorage, incrementChatMessageCount, getChatMessageCount, resetChatMessageCount, getSessionId, getUserInfo, getKnownFactsForDetection, getCurrentSubject, setCurrentSubject, setSubjectPromptText, setSubjectPromptAgentsUsed, getSubjectPromptAgentsUsed, setChatHistory, getEnableSubjectTracking, setEnableSubjectTracking, getLockCurrentSubject, setLockCurrentSubject } from '../services/sessionStorageService';
import { postSessionStat } from '../services/apiService';
import logger from '../utils/logger';

const ChatbotPage = ({ accessToken, loadedSavedChat, onSavedChatLoaded }) => {
  const { conversationHistory, addMessage, clearHistory, initializeWithSystemPrompt, getCurrentHistory, refreshFromStorage, setConversationHistory } = useChatHistory();
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationIdState] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [initError, setInitError] = useState(null);
  const [currentSubject, setCurrentSubjectState] = useState('general');
  const [enableSubjectTracking, setEnableSubjectTrackingState] = useState(true);
  const [lockCurrentSubject, setLockCurrentSubjectState] = useState(false);
  const pendingSubjectTrackingChangeRef = useRef(null);
  const pendingLockSubjectChangeRef = useRef(null);
  const pendingResetSubjectRef = useRef(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const lastScrollIntentRef = useRef('init'); // 'init' | 'user' | 'ai' | 'none'

  // Helper function to update currentSubject in both state and sessionStorage
  const updateCurrentSubject = (subject) => {
    setCurrentSubjectState(subject);
    setCurrentSubject(subject);
  };

  // Helper function to update subject-related sessionStorage and rebuild dynamic system prompt
  const updateSubjectAndRebuildPrompt = useCallback((newSubject, subjectPromptText = '', agentsUsed = false) => {
    // Update sessionStorage variables
    setSubjectPromptText(subjectPromptText);
    setSubjectPromptAgentsUsed(agentsUsed);
    updateCurrentSubject(newSubject);
    
    // Rebuild dynamic system prompt
    try {
      build_dynamic_system_prompt();
      logger.info('Dynamic system prompt rebuilt successfully', { subject: newSubject });
    } catch (error) {
      logger.warn('Dynamic system prompt rebuild failed, continuing with chat', { 
        subject: newSubject, 
        error: error.message 
      });
    }
  }, []);

  // Function to apply reset subject actions (called immediately or deferred based on isLoading state)
  const applyResetSubject = useCallback(() => {
    // 1. Enable subject tracking
    setEnableSubjectTracking(true);
    setEnableSubjectTrackingState(true);
    
    // 2. Unlock subject
    setLockCurrentSubject(false);
    setLockCurrentSubjectState(false);
    
    // 3. Reset subject to "none" and clear subject prompt text
    // This also rebuilds the dynamic system prompt
    updateSubjectAndRebuildPrompt('none', '', false);
    
    logger.info('Subject reset completed');
  }, [updateSubjectAndRebuildPrompt]);

  // Function to apply subject tracking change (called immediately or deferred based on isLoading state)
  // This function handles the actual subject/prompt updates when tracking is enabled/disabled
  const applySubjectTrackingChange = useCallback((checked) => {
    if (!checked) {
      // Disable: reset subject to "none" and clear subject prompt text
      // This ensures subject classification is effectively disabled
      updateSubjectAndRebuildPrompt('none', '', false);
      logger.info('Subject tracking disabled - subject reset to "none"');
    } else {
      // Enable: no immediate action needed
      // Subject stays "none" until next classification occurs naturally
      // We don't auto-classify when re-enabling to avoid unexpected behavior
      logger.info('Subject tracking enabled - will classify on next message');
    }
  }, [updateSubjectAndRebuildPrompt]);

  // Checkbox change handler with deferred update pattern to prevent race conditions
  // Defers subject/prompt updates when request is in flight to prevent interference with in-progress classification
  const handleEnableSubjectTrackingChange = useCallback((e) => {
    const checked = e.target.checked;
    
    // Update component state immediately for responsive UI feedback
    setEnableSubjectTrackingState(checked);
    
    // Update sessionStorage immediately to persist the user's choice
    setEnableSubjectTracking(checked);
    
    // Clear lock when tracking is disabled (lock depends on tracking being enabled)
    if (!checked) {
      setLockCurrentSubject(false);
      setLockCurrentSubjectState(false);
      logger.info('Lock cleared - Enable Subject Tracking is disabled');
    }
    
    // Defer subject/prompt update if request is in flight to prevent race conditions
    if (isLoading) {
      // Store pending change in ref for application after request completes
      pendingSubjectTrackingChangeRef.current = checked;
      logger.info('Deferring subject tracking change until current request completes', { checked });
      return; // Early return - update will be applied in finally block
    }
    
    // No request in flight - apply change immediately
    applySubjectTrackingChange(checked);
  }, [isLoading, applySubjectTrackingChange]);

  // Helper function to update system message in chat history with new dynamic system prompt
  const updateSystemMessageInChatHistory = (subject) => {
    try {
      const currentHistory = getCurrentHistory();
      const systemMessageIndex = currentHistory.findIndex(msg => msg.role === 'system');
      const newDynamicPrompt = getDynamicSystemPromptFromStorage();
      
      if (newDynamicPrompt) {
        const updatedHistory = [...currentHistory];
        
        if (systemMessageIndex !== -1) {
          // Case 1: System message exists - update it (preserve original timestamp)
          updatedHistory[systemMessageIndex] = {
            ...updatedHistory[systemMessageIndex], // Preserve original timestamp and metadata
            content: newDynamicPrompt.prompt
          };
          logger.info('System message updated in chat history for subject change', { subject });
        } else {
          // Case 2: No system message found - insert new one at beginning
          const newSystemMessage = {
            role: 'system',
            content: newDynamicPrompt.prompt,
            timestamp: new Date().toISOString(),
            metadata: {
              subject: newDynamicPrompt.subject,
              category: newDynamicPrompt.category,
              version: newDynamicPrompt.version,
              last_updated: newDynamicPrompt.last_updated
            }
          };
          updatedHistory.unshift(newSystemMessage);
          logger.info('System message added to chat history for subject change', { subject });
        }
        
        // Update both sessionStorage and React state for UI consistency
        setChatHistory(updatedHistory);
        setConversationHistory(updatedHistory);
      }
    } catch (error) {
      logger.warn('Failed to update system message in chat history, continuing with chat', { 
        subject, 
        error: error.message 
      });
    }
  };

  // Function to format AI response text with proper formatting
  const formatMessageContent = (content) => {
    if (!content) return '';
    
    // Convert line breaks to <br> tags
    let formattedContent = content.replace(/\n/g, '<br>');
    
    // Convert markdown-style formatting
    // Bold: **text** or __text__ -> <strong>text</strong>
    formattedContent = formattedContent.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    formattedContent = formattedContent.replace(/__(.*?)__/g, '<strong>$1</strong>');
    
    // Italic: *text* or _text_ -> <em>text</em>
    formattedContent = formattedContent.replace(/\*(.*?)\*/g, '<em>$1</em>');
    formattedContent = formattedContent.replace(/_(.*?)_/g, '<em>$1</em>');
    
    // Code: `code` -> <code>code</code>
    formattedContent = formattedContent.replace(/`(.*?)`/g, '<code>$1</code>');
    
    // Code blocks: ```code``` -> <pre><code>code</code></pre>
    formattedContent = formattedContent.replace(/```(.*?)```/gs, '<pre><code>$1</code></pre>');
    
    return formattedContent;
  };

  // Scroll helpers (container-based)
  const scrollToBottom = (smooth = true) => {
    const container = messagesContainerRef.current;
    if (!container) return;
    if (smooth) {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    } else {
      container.scrollTop = container.scrollHeight;
    }
  };
  const scrollContainerToTop = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = 0;
    }
  };

  // Centralized scroll behavior based on intent
  useLayoutEffect(() => {
    if (isInitializing) {
      // Wait for initialization to complete before deciding scroll position
      return;
    }

    if (lastScrollIntentRef.current === 'init') {
      // On entering the page (or after refresh), show latest message if any
      const hasNonSystemMessages = conversationHistory.some(m => m.role !== 'system');
      if (hasNonSystemMessages) {
        scrollToBottom(false); // instant within container
      } else {
        scrollContainerToTop();
      }
      lastScrollIntentRef.current = 'none';
    } else if (lastScrollIntentRef.current === 'user' || lastScrollIntentRef.current === 'ai') {
      // Smooth for user send; AI can be smooth as well
      const smooth = lastScrollIntentRef.current === 'user';
      scrollToBottom(smooth);
      lastScrollIntentRef.current = 'none';
    }
  }, [conversationHistory, isInitializing]);

  // Handle loaded saved chat
  useEffect(() => {
    if (loadedSavedChat && onSavedChatLoaded) {
      // Flush chat_messages on Restore Saved Chat boundary
      try {
        const count = getChatMessageCount();
        const sessionId = getSessionId();
        if (sessionId && accessToken && count > 0) {
          (async () => {
            try { await postSessionStat({ session_id: sessionId, event: 'chat_messages', count }, accessToken); } catch {}
            resetChatMessageCount();
          })();
        } else {
          resetChatMessageCount();
        }
      } catch {}

      logger.info('Loading saved chat into ChatbotPage', { 
        chatName: loadedSavedChat.chat_name,
        messageCount: loadedSavedChat.messages.length,
        subject: loadedSavedChat.subject
      });
      
      // Clear current conversation and load saved chat
      lastScrollIntentRef.current = 'init';
      clearHistory();
      setConversationHistory(loadedSavedChat.messages);
      
      // Conditional subject restoration based on tracking state
      // If tracking is enabled, restore subject from saved chat (current behavior)
      // If tracking is disabled, ignore saved chat subject and keep current subject as "none"
      // This ensures consistent behavior with user's tracking preference
      const isTrackingEnabled = getEnableSubjectTracking();
      if (isTrackingEnabled) {
        // Use the saved chat's subject, defaulting to 'none' if not present (defensive coding)
        const savedSubject = loadedSavedChat.subject || 'none';
        const savedSubjectPromptText = ''; // Always reset subject prompt text for loaded chats
        
        // Update subject and rebuild dynamic system prompt with saved subject
        updateSubjectAndRebuildPrompt(savedSubject, savedSubjectPromptText, false);
      } else {
        // Tracking disabled: ignore saved chat subject, keep current subject as "none"
        logger.info('Subject restoration skipped - subject tracking is disabled', {
          savedChatSubject: loadedSavedChat.subject
        });
        // Current subject remains "none" (already set on login or when tracking was disabled)
      }
      
      // Clear lock on saved chat restoration to allow subject classification to resume
      setLockCurrentSubject(false);
      setLockCurrentSubjectState(false);
      logger.info('Lock cleared - saved chat restored');
      
      // Update conversation ID
      const newConversationId = crypto.randomUUID();
      setConversationId(newConversationId);
      setConversationIdState(newConversationId);
      
      // Notify that saved chat has been loaded
      onSavedChatLoaded();
    }
  }, [loadedSavedChat, onSavedChatLoaded, clearHistory, setConversationHistory, accessToken, updateSubjectAndRebuildPrompt]);

  // Initialize currentSubject from sessionStorage on component mount
  useEffect(() => {
    const storedSubject = getCurrentSubject();
    setCurrentSubjectState(storedSubject);
  }, []);

  // Initialize enableSubjectTracking from sessionStorage on component mount
  // Component mount occurs on first visit and every time user navigates back to the page
  // This ensures checkbox state is always synced with sessionStorage when the page is visited
  useEffect(() => {
    const storedValue = getEnableSubjectTracking();
    setEnableSubjectTrackingState(storedValue);
  }, []);

  // Initialize lockCurrentSubject from sessionStorage on component mount
  // Component mount occurs on first visit and every time user navigates back to the page
  // This ensures checkbox state is always synced with sessionStorage when the page is visited
  useEffect(() => {
    const storedValue = getLockCurrentSubject();
    setLockCurrentSubjectState(storedValue);
  }, []);

  // Initialize chat when component mounts
  useEffect(() => {
    const initializeChat = async () => {
      if (!accessToken) {
        setIsInitializing(false);
        return;
      }

      try {
        setInitError(null);
        lastScrollIntentRef.current = 'init';

        // Get or create conversation ID
        let currentConversationId = getConversationId();
        if (!currentConversationId) {
          currentConversationId = crypto.randomUUID();
          setConversationId(currentConversationId);
        }
        setConversationIdState(currentConversationId);

        // Initialize with dynamic system prompt from sessionStorage if we don't have it in chat history
        const currentHistory = getCurrentHistory();
        if (currentHistory.length === 0 || currentHistory[0].role !== 'system') {
          const dynamicSystemPromptData = getDynamicSystemPromptFromStorage();
          if (dynamicSystemPromptData) {
            logger.info('Initializing chat with dynamic system prompt from sessionStorage');
            initializeWithSystemPrompt(dynamicSystemPromptData);
          } else {
            logger.error('No dynamic system prompt found in sessionStorage - this should not happen after login');
            setInitError('Dynamic system prompt not available. Please log out and log back in.');
          }
        }

        setIsInitializing(false);
      } catch (error) {
        logger.error('Error initializing chat', { error: error.message });
        setInitError('Failed to initialize chat. Please refresh the page.');
        setIsInitializing(false);
      }
    };

    initializeChat();
  }, [accessToken, getCurrentHistory, initializeWithSystemPrompt]);

  // Focus input field when initialization is complete and refresh from sessionStorage
  useEffect(() => {
    if (!isInitializing && !initError) {
      // Fix: Auto-recovery for navigation edge cases
      // Refresh chat history from sessionStorage when component becomes ready
      // This ensures any API responses received while component was unmounted are displayed
      // (e.g., user navigates away before response arrives, then returns to chat)
      lastScrollIntentRef.current = 'init';
      refreshFromStorage();
      
      setTimeout(() => {
        inputRef.current?.focus({ preventScroll: true });
      }, 100);
    }
  }, [isInitializing, initError, refreshFromStorage]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading || !accessToken || !conversationId) return;

    const userMessage = {
      role: 'user',
      content: inputMessage.trim(),
      timestamp: new Date().toISOString()
    };

    // Add user message to conversation history
    lastScrollIntentRef.current = 'user';
    addMessage(userMessage);
    // Increment per-tab chat message counter (ignore blanks already filtered)
    try { incrementChatMessageCount(); } catch {}
    const currentInput = inputMessage.trim();
    setInputMessage('');
    setIsLoading(true);

    // User fact detection - call backend API before sending chat message
    try {
      const userInfo = getUserInfo();
      const userId = userInfo?.user_id || userInfo?.username || 'unknown';
      const knownFacts = getKnownFactsForDetection();
      
      logger.debug('Calling user fact detection', { 
        userId, 
        messageLength: currentInput.length, 
        knownFactsCount: knownFacts.length 
      });
      
      await detectUserInfo(userId, currentInput, knownFacts, accessToken);
      
      logger.info('User fact detection completed successfully', { userId });
    } catch (error) {
      // Log error but don't block chat flow - fact detection is best-effort
      logger.warn('User fact detection failed, continuing with chat', { 
        error: error.message,
        message: currentInput.substring(0, 50) + '...' // Log first 50 chars for debugging
      });
    }

    // Subject classification - skip if tracking disabled OR lock enabled
    // Both tracking enabled AND lock unlocked must be true for classification to proceed
    try {
      const isTrackingEnabled = getEnableSubjectTracking();
      const isLocked = getLockCurrentSubject();
      if (!isTrackingEnabled || isLocked) {
        if (!isTrackingEnabled) {
          logger.info('Subject classification skipped - subject tracking is disabled');
        } else if (isLocked) {
          logger.info('Subject classification skipped - subject is locked');
        }
        // Skip entire classification block when tracking is disabled or subject is locked
      } else {
        // Get current history and add the new user message for classification
        const currentHistory = getCurrentHistory();
        const chatHistoryWithNewMessage = [...currentHistory, userMessage];
        
        // Filter out system messages for classification (external agent may be confused by them)
        // The system prompt contains detailed instructions and user profile information that can
        // interfere with the subject detection agent's ability to classify the actual conversation topic
        const chatHistoryForClassification = chatHistoryWithNewMessage.filter(msg => msg.role !== 'system');
        
        logger.debug('Calling subject classification', { 
          originalMessageCount: chatHistoryWithNewMessage.length,
          filteredMessageCount: chatHistoryForClassification.length,
          systemMessagePresent: chatHistoryWithNewMessage.some(msg => msg.role === 'system')
        });
        
          const classificationResponse = await classifySubject(chatHistoryForClassification, accessToken);
        
        // Check for pending checkbox change before applying classification results
        // If user toggled checkbox during classification, ignore the results to prevent race conditions
        // Also check if lock was enabled during classification - if so, ignore results
        if (pendingSubjectTrackingChangeRef.current !== null) {
          logger.info('Subject classification result ignored due to pending checkbox change', {
            pendingChange: pendingSubjectTrackingChangeRef.current
          });
          // Don't apply classification results - they will be handled by deferred update
        } else if (pendingLockSubjectChangeRef.current === true) {
          logger.info('Subject classification result ignored due to pending lock change', {
            pendingLockChange: pendingLockSubjectChangeRef.current
          });
          // Don't apply classification results - lock was enabled during classification
        } else if (classificationResponse.success && classificationResponse.data) {
          const subject = classificationResponse.data.subject;
          const confidence = classificationResponse.data.confidence;
          
          logger.info('Subject classification completed', { subject, confidence });
          
          // Log to console as requested
          console.log('🎯 Conversation Subject:', subject, `(confidence: ${confidence})`);
          
          // Compare with current subject and update if changed
          const storedSubject = getCurrentSubject();
          if (subject !== storedSubject) {
            logger.info('Subject changed, attempting to retrieve subject prompt', { 
              oldSubject: storedSubject, 
              newSubject: subject 
            });
            
            try {
              // Retrieve subject-specific prompt
              const subjectPromptData = await getSubjectPrompt(subject, 'GPT', accessToken);
              
              if (subjectPromptData && subjectPromptData.promptText) {
                logger.info('Subject prompt retrieved successfully', {
                  subject,
                  promptLength: subjectPromptData.promptText.length,
                  agentsUsed: subjectPromptData.agentsUsed
                });
                
                // Update subject and rebuild dynamic system prompt
                updateSubjectAndRebuildPrompt(subject, subjectPromptData.promptText, subjectPromptData.agentsUsed);
                
                // Update system message in chat history to match new dynamic system prompt
                updateSystemMessageInChatHistory(subject);
              } else {
                logger.warn('No subject prompt found, keeping current subject', {
                  requestedSubject: subject,
                  currentSubject: storedSubject
                });
              }
            } catch (error) {
              logger.warn('Subject prompt retrieval failed, keeping current subject', {
                requestedSubject: subject,
                currentSubject: storedSubject,
                error: error.message
              });
            }
          }
        } else {
          logger.warn('Subject classification returned no data');
        }
      }
    } catch (error) {
      // Log error but don't block chat flow - subject classification is best-effort
      logger.warn('Subject classification failed, continuing with chat', { 
        error: error.message 
      });
    }

    try {
      // Get the current conversation history from sessionStorage (source of truth)
      const chatHistory = getCurrentHistory();
      const currentSubject = getCurrentSubject();
      const subjectToSend = currentSubject && currentSubject !== 'none' ? currentSubject : null;
      const agentToSend = 'GPT';
      const agentsUsedToSend = subjectToSend ? getSubjectPromptAgentsUsed() : false;

      logger.debug('Sending message with conversation history', {
        messageCount: chatHistory.length,
        subject: subjectToSend,
        agent: agentToSend,
        agents_used: agentsUsedToSend
      });

      const response = await sendChatMessage(
        currentInput,
        chatHistory, // Include the full chat history including system prompt
        accessToken,
        subjectToSend,
        agentToSend,
        agentsUsedToSend,
        conversationId,
        0.7, // temperature
        1000 // max_tokens
      );

      if (response.success && response.data && response.data.response) {
        const botMessage = {
          role: 'assistant',
          content: response.data.response,
          timestamp: response.data.timestamp || new Date().toISOString(),
          metadata: {
            message_id: response.data.message_id,
            tokens_used: response.data.tokens_used,
            model_used: response.data.model_used
          }
        };
        
        // Add bot response to conversation history
        lastScrollIntentRef.current = 'ai';
        addMessage(botMessage);
        
        logger.debug('AI response received, conversation history now has', { messageCount: getCurrentHistory().length });
        
        // Focus input field after AI response
        setTimeout(() => {
          inputRef.current?.focus({ preventScroll: true });
        }, 100);
      } else {
        const errorMessage = {
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again.',
          timestamp: new Date().toISOString()
        };
        addMessage(errorMessage);
      }
    } catch (error) {
              logger.error('Error sending message', { error: error.message });
      const errorMessage = {
        role: 'assistant',
        content: 'Network error. Please check your connection.',
        timestamp: new Date().toISOString()
      };
      addMessage(errorMessage);
    } finally {
      setIsLoading(false);
      
      // Apply pending checkbox change after request completes (deferred update pattern)
      if (pendingSubjectTrackingChangeRef.current !== null) {
        const pendingChange = pendingSubjectTrackingChangeRef.current;
        pendingSubjectTrackingChangeRef.current = null;
        logger.info('Applying deferred subject tracking change', { checked: pendingChange });
        applySubjectTrackingChange(pendingChange);
      }
      
      // Clear pending lock change ref (lock state already updated in sessionStorage)
      if (pendingLockSubjectChangeRef.current !== null) {
        pendingLockSubjectChangeRef.current = null;
        logger.info('Cleared pending lock subject change ref');
      }
      
      // Apply pending reset subject if any
      if (pendingResetSubjectRef.current) {
        pendingResetSubjectRef.current = false;
        logger.info('Applying deferred reset subject');
        applyResetSubject();
      }
    }
  };

  // TODO: Remove this debug function once verification is complete
  const handleDebugSystemMessage = () => {
    const currentHistory = getCurrentHistory();
    const chatHistorySystemMessage = currentHistory.find(msg => msg.role === 'system');
    const dynamicSystemPrompt = getDynamicSystemPromptFromStorage();
    
    // Create popup with options
    const options = [];
    if (chatHistorySystemMessage) {
      options.push('Chat History System Prompt');
    }
    if (dynamicSystemPrompt) {
      options.push('Dynamic System Prompt from sessionStorage');
    }
    
    if (options.length === 0) {
      alert('No system prompts found in either location.');
      return;
    }
    
    const choice = prompt(`Select system prompt to display:\n\n${options.map((opt, i) => `${i + 1}. ${opt}`).join('\n')}\n\nEnter number (1-${options.length}):`);
    
    if (!choice) return; // User cancelled
    
    const choiceIndex = parseInt(choice) - 1;
    if (choiceIndex < 0 || choiceIndex >= options.length) {
      alert('Invalid selection.');
      return;
    }
    
    const selectedOption = options[choiceIndex];
    
    // Clear console and add separator
    console.clear();
    console.log('='.repeat(80));
    console.log('🔍 DEBUG SYSTEM PROMPT DISPLAY');
    console.log('='.repeat(80));
    
    if (selectedOption === 'Chat History System Prompt') {
      console.log('📋 DISPLAYING: Chat History System Prompt');
      console.log('📍 SOURCE: sessionStorage key "chatHistory" -> message with role="system"');
      console.log('⏰ TIMESTAMP:', chatHistorySystemMessage.timestamp);
      console.log('📊 METADATA:', chatHistorySystemMessage.metadata);
      console.log('─'.repeat(80));
      console.log('📝 SYSTEM PROMPT CONTENT:');
      console.log('─'.repeat(80));
      console.log(chatHistorySystemMessage.content);
      console.log('─'.repeat(80));
      console.log('✅ END OF: Chat History System Prompt');
    } else if (selectedOption === 'Dynamic System Prompt from sessionStorage') {
      console.log('📋 DISPLAYING: Dynamic System Prompt from sessionStorage');
      console.log('📍 SOURCE: sessionStorage key "dynamicSystemPrompt"');
      console.log('📊 METADATA:', {
        subject: dynamicSystemPrompt.subject,
        category: dynamicSystemPrompt.category,
        version: dynamicSystemPrompt.version,
        last_updated: dynamicSystemPrompt.last_updated
      });
      console.log('─'.repeat(80));
      console.log('📝 SYSTEM PROMPT CONTENT:');
      console.log('─'.repeat(80));
      console.log(dynamicSystemPrompt.prompt);
      console.log('─'.repeat(80));
      console.log('✅ END OF: Dynamic System Prompt from sessionStorage');
    }
    
    console.log('='.repeat(80));
  };

  // Lock Subject checkbox handler with deferred update pattern to prevent race conditions
  const handleLockSubjectChange = useCallback((e) => {
    const checked = e.target.checked;
    
    // Update component state immediately for responsive UI feedback
    setLockCurrentSubjectState(checked);
    
    // Update sessionStorage immediately to persist the user's choice
    setLockCurrentSubject(checked);
    
    // Defer ref update if request is in flight (lock is just a flag, no additional action needed)
    if (isLoading) {
      pendingLockSubjectChangeRef.current = checked;
      logger.info('Deferring lock subject change until current request completes', { checked });
      return; // Early return - ref will be cleared in finally block
    }
    
    // No request in flight - no additional action needed (lock is just a flag)
  }, [isLoading]);

  // Reset Subject button handler with deferred update pattern to prevent race conditions
  const handleResetSubject = useCallback(() => {
    // Check if request is in flight - defer if so
    if (isLoading) {
      pendingResetSubjectRef.current = true;
      logger.info('Deferring reset subject until current request completes');
      return;
    }
    
    // Apply reset immediately
    applyResetSubject();
  }, [isLoading, applyResetSubject]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleClearChat = async () => {
    // Flush chat_messages count if > 0 (New Chat boundary)
    try {
      const count = getChatMessageCount();
      const sessionId = getSessionId();
      if (sessionId && accessToken && count > 0) {
        try { await postSessionStat({ session_id: sessionId, event: 'chat_messages', count }, accessToken); } catch {}
      }
      resetChatMessageCount();
    } catch {}

    if (conversationId && accessToken) {
      try {
        // Call backend to clear conversation (placeholder endpoint)
        await clearChat(conversationId, accessToken);
      } catch (error) {
        logger.error('Failed to clear chat on backend', { error: error.message });
        // Continue with frontend clear even if backend fails
      }
    }
    
    // Clear conversation history from sessionStorage
    lastScrollIntentRef.current = 'init';
    clearHistory();
    
    // Reset subject and rebuild dynamic system prompt
    updateSubjectAndRebuildPrompt('none', '');
    
    // Clear lock on clear chat to allow subject classification to resume
    setLockCurrentSubject(false);
    setLockCurrentSubjectState(false);
    logger.info('Lock cleared - chat cleared');
    
    // Remove conversation ID and create a new one
    removeConversationId();
    const newConversationId = crypto.randomUUID();
    setConversationId(newConversationId);
    setConversationIdState(newConversationId);
    
    // Re-initialize with dynamic system prompt from sessionStorage
    if (accessToken) {
      try {
        const dynamicSystemPromptData = getDynamicSystemPromptFromStorage();
        if (dynamicSystemPromptData) {
          initializeWithSystemPrompt(dynamicSystemPromptData);
        } else {
          logger.error('No dynamic system prompt found in sessionStorage after clear');
        }
      } catch (error) {
        logger.error('Failed to re-initialize system prompt after clear', { error: error.message });
      }
    }
  };

  return (
    <div className="chatbot-page">
      <div className="chat-header">
        <div className="chat-header-content">
          <img 
            src="/images/jj_icon_character_profilepicture.png" 
            alt="Jeeves Junior" 
            className="chat-avatar"
          />
          <h1 className="chat-title">
            CHATTING WITH <span className="junior-text">JUNIOR</span>
          </h1>
        </div>
      </div>
      
      <div className="chat-container">
        <div className="messages-container" ref={messagesContainerRef}>
          {isInitializing && (
            <div className="welcome-message">
              <p>Initializing chat session...</p>
            </div>
          )}
          
          {initError && (
            <div className="welcome-message">
              <p style={{ color: '#cc6666' }}>{initError}</p>
            </div>
          )}
          
          {!isInitializing && !initError && conversationHistory.length <= 1 && (
            <div className="welcome-message">
              <p>Hello! I'm Jeeves Junior, your AI assistant. How can I help you today?</p>
            </div>
          )}
          
          {conversationHistory.filter(msg => msg.role !== 'system').map((message, index) => (
            <div 
              key={index} 
              className={`message ${message.role === 'user' ? 'user-message' : 'bot-message'}`}
            >
              <div className="message-content">
                {message.role === 'assistant' ? (
                  <div 
                    dangerouslySetInnerHTML={{ 
                      __html: formatMessageContent(message.content) 
                    }} 
                  />
                ) : (
                  message.content
                )}
              </div>
              <div className="message-timestamp">
                {new Date(message.timestamp).toLocaleTimeString()}
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="message bot-message">
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
        
        <div className="chat-input-container">
          <div className="input-wrapper">
            <input
              ref={inputRef}
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="How can I help?"
              className="chat-input"
              disabled={isLoading || isInitializing || !!initError}
            />
            <button 
              onClick={sendMessage}
              disabled={!inputMessage.trim() || isLoading || isInitializing || !!initError}
              className="send-button"
            >
              <img 
                src="/images/jj_icon_send.png" 
                alt="Send" 
                className="send-icon"
              />
            </button>
          </div>
        </div>
        
        <div className="chat-actions">
          <div className="chat-actions-left-group">
            <div className="chat-actions-section">
              <img 
                src="/images/jj_icon_clearchat1.png" 
                alt="Clear Chat" 
                className="clear-chat-icon"
                onClick={handleClearChat}
                style={{ cursor: 'pointer' }}
              />
            </div>
            <div className="section-separator"></div>
            <div className="chat-options-section">
              <div className="subject-display">
                <span className="subject-label">Subject:</span>
                <span className="subject-value">{currentSubject}</span>
                <label className="enable-subject-tracking-label">
                  <input
                    type="checkbox"
                    className="enable-subject-tracking-checkbox"
                    checked={enableSubjectTracking}
                    onChange={handleEnableSubjectTrackingChange}
                  />
                  Enable Subject Tracking
                </label>
                <label className="lock-subject-label">
                  <input
                    type="checkbox"
                    className="lock-subject-checkbox"
                    checked={lockCurrentSubject}
                    onChange={handleLockSubjectChange}
                    disabled={!enableSubjectTracking}
                  />
                  Lock Subject
                </label>
                <button
                  className="reset-subject-button"
                  onClick={handleResetSubject}
                  disabled={isLoading}
                  title="Reset subject to 'none', enable tracking, and unlock subject"
                >
                  Reset Subject
                </button>
              </div>
            </div>
          </div>
          {process.env.NODE_ENV === 'development' && (
            <>
              <div className="section-separator"></div>
              <div className="debug-section">
                <button 
                  onClick={handleDebugSystemMessage}
                  className="debug-system-button"
                >
                  Debug System
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatbotPage; 